/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { KAI_SHIKI_LAYOUT, KAI_SHIKI_TYPOGRAPHY } from '@/Client/Styles/kaiShikiTokens';
import { PageLayout } from '../Layout';
import { usePageTitle } from '../Lib';
import { useAuth, useKaiProApiClient, useToast } from '../State';
import { Button } from '../Widgets';

const MAX_SIZE_BYTES = 100 * 1024 * 1024;

const PAGE_STYLE = css`
  label: ISSUE_VIDEO_UPLOAD_PAGE_STYLE;
  max-width: 48rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${KAI_SHIKI_LAYOUT.spacing2}px;

  @media (max-width: 900px) {
    max-width: none;
    gap: ${KAI_SHIKI_LAYOUT.spacing1}px;
  }
`;

const CARD_STYLE = css`
  label: ISSUE_VIDEO_UPLOAD_CARD_STYLE;
  background: var(--background);
  border: 1px solid var(--border);
  padding: ${KAI_SHIKI_LAYOUT.spacing3}px;
  display: flex;
  flex-direction: column;
  gap: ${KAI_SHIKI_LAYOUT.spacing2}px;

  @media (max-width: 900px) {
    padding: ${KAI_SHIKI_LAYOUT.spacing2}px;
  }
`;

const INTRO_STYLE = css`
  label: ISSUE_VIDEO_UPLOAD_INTRO_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.medium}
  color: var(--text2);
`;

const LABEL_STYLE = css`
  label: ISSUE_VIDEO_UPLOAD_LABEL_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.smallBold}
  color: var(--text);
  text-transform: uppercase;
`;

const INPUT_STYLE = css`
  label: ISSUE_VIDEO_UPLOAD_INPUT_STYLE;
  width: 100%;
  border: 1px solid var(--border);
  background: var(--background2);
  color: var(--text);
  padding: ${KAI_SHIKI_LAYOUT.spacing1}px;
  ${KAI_SHIKI_TYPOGRAPHY.medium}
`;

const TEXTAREA_STYLE = css`
  label: ISSUE_VIDEO_UPLOAD_TEXTAREA_STYLE;
  ${INPUT_STYLE}
  min-height: 10rem;
  resize: vertical;

  @media (max-width: 900px) {
    min-height: 8rem;
  }
`;

const STATUS_STYLE = css`
  label: ISSUE_VIDEO_UPLOAD_STATUS_STYLE;
  background: var(--background2);
  border: 1px solid var(--border);
  padding: ${KAI_SHIKI_LAYOUT.spacing2}px;
  white-space: pre-wrap;
  ${KAI_SHIKI_TYPOGRAPHY.small}
  color: var(--text2);
`;

const LIST_STYLE = css`
  label: ISSUE_VIDEO_UPLOAD_LIST_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${KAI_SHIKI_LAYOUT.spacing2}px;
`;

const ITEM_STYLE = css`
  label: ISSUE_VIDEO_UPLOAD_ITEM_STYLE;
  background: var(--background);
  border: 1px solid var(--border);
  padding: ${KAI_SHIKI_LAYOUT.spacing2}px;
  display: flex;
  flex-direction: column;
  gap: ${KAI_SHIKI_LAYOUT.spacing1}px;
`;

const MOBILE_PAGE_STYLE = css`
  label: ISSUE_VIDEO_UPLOAD_MOBILE_PAGE_STYLE;
  height: 100dvh;
  background: var(--background2);
  padding: ${KAI_SHIKI_LAYOUT.spacing2}px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
`;

const putFileWithProgress = (
  url: string,
  file: File,
  onProgress: (progress: number) => void,
) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.addEventListener('progress', event => {
      if (!event.lengthComputable) return;
      onProgress(Math.round((event.loaded / event.total) * 100));
    });
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed with status ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Network error during S3 upload'));
    xhr.send(file);
  });

export const IssueVideoUploadPage = ({ mobileStandalone = false }: { mobileStandalone?: boolean }) => {
  usePageTitle('Issue Video Upload');
  const kaiProApiClient = useKaiProApiClient();
  const { credentials, user } = useAuth();
  const { showToast, showErrorToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [projectId, setProjectId] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState('Choose an optional project, add an optional note, and upload a short video.');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  const loadSubmissions = useCallback(async (nextProjectId?: string) => {
    try {
      setLoadingSubmissions(true);
      const response = await kaiProApiClient.listIssueVideoSubmissions({
        projectId: nextProjectId?.trim() || undefined,
        userId: user?.id,
        limit: 10,
      });
      setSubmissions(response.submissions);
    } catch (error: any) {
      setSubmissions([]);
      setStatus(error?.message || 'Failed to load submissions');
    } finally {
      setLoadingSubmissions(false);
    }
  }, [kaiProApiClient, user?.id]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void loadSubmissions(projectId || undefined);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [loadSubmissions, projectId]);

  const upload = async () => {
    if (!credentials.token) {
      showErrorToast('You must be logged in to upload a video.');
      return;
    }
    if (!file) {
      showErrorToast('Pick a video file first.');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      showErrorToast('This MVP only supports videos up to 100 MB.');
      return;
    }

    try {
      setUploading(true);
      setProgress(0);
      setStatus('Requesting upload URL from Kai...');

      const init = await kaiProApiClient.initIssueVideoUpload({
        projectId: projectId.trim() || undefined,
        note: note.trim() || undefined,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      });

      setStatus('Uploading directly to secure storage...');
      await putFileWithProgress(init.uploadUrl, file, setProgress);

      setStatus('Finalizing submission...');
      await kaiProApiClient.completeIssueVideoUpload({
        issueVideoId: init.issueVideoId,
        objectKey: init.objectKey,
        sizeBytes: file.size,
        note: note.trim() || undefined,
      });

      setStatus('Upload complete. Kai can now review the video.');
      setProgress(100);
      setNote('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadSubmissions(projectId || undefined);
      showToast('Issue video uploaded.');
    } catch (error: any) {
      const message = error?.message || 'Upload failed';
      setStatus(message);
      setProgress(0);
      showErrorToast(message);
    } finally {
      setUploading(false);
    }
  };

  const content = (
    <div css={PAGE_STYLE}>
      <div css={CARD_STYLE}>
        <div css={INTRO_STYLE}>
          Upload a short issue video from the normal Kai app. Kai will authorize the upload, send the file directly to S3, and store the submission metadata
          when the upload completes. This page only shows videos uploaded by your account, even if you are a super admin.
        </div>

        <label css={LABEL_STYLE} htmlFor='projectId'>Project ID (optional)</label>
        <input
          id='projectId'
          css={INPUT_STYLE}
          value={projectId}
          onChange={event => setProjectId(event.target.value)}
          placeholder='property-project uuid'
        />

        <label css={LABEL_STYLE} htmlFor='note'>Note</label>
        <textarea
          id='note'
          css={TEXTAREA_STYLE}
          value={note}
          onChange={event => setNote(event.target.value)}
          placeholder='Describe the issue shown in the video.'
        />

        <label css={LABEL_STYLE} htmlFor='videoFile'>Video</label>
        <input
          id='videoFile'
          ref={fileInputRef}
          css={INPUT_STYLE}
          type='file'
          accept='video/*'
          capture='environment'
          onChange={event => setFile(event.target.files?.[0] ?? null)}
        />

        <div css={INTRO_STYLE}>
          Max size: 100 MB. Supported types: MP4, MOV, WebM. The list below shows your recent uploads.
        </div>

        <Button onClick={upload} disabled={uploading || !file} buttonStyle='darkBlue'>
          {uploading ? 'Uploading...' : 'Upload Video'}
        </Button>

        <progress value={progress} max={100} style={{ width: '100%' }} />
        <div css={STATUS_STYLE}>{status}</div>
      </div>

      <div css={CARD_STYLE}>
        <div css={LABEL_STYLE}>Recent Submissions</div>
        <div css={INTRO_STYLE}>
          {loadingSubmissions
            ? 'Loading submissions...'
            : projectId.trim()
            ? 'Only your uploads for this project.'
            : 'Only your uploads are shown here.'}
        </div>

        <div css={LIST_STYLE}>
          {submissions.map(submission => (
            <div key={submission.id} css={ITEM_STYLE}>
              <div css={LABEL_STYLE}>{submission.originalFileName}</div>
              <div css={INTRO_STYLE}>Uploaded {new Date(submission.createdAt).toLocaleString()}</div>
              <div css={INTRO_STYLE}>Issue ID: {submission.id}</div>
              {submission.note ? <div css={INTRO_STYLE}>{submission.note}</div> : null}
              {submission.playbackUrl
                ? (
                  <video
                    controls
                    preload='metadata'
                    style={{ width: '100%', maxHeight: '24rem', background: '#000' }}
                    src={submission.playbackUrl}
                  />
                )
                : null}
            </div>
          ))}

          {!loadingSubmissions && submissions.length === 0
            ? (
              <div css={INTRO_STYLE}>
                {projectId.trim() ? 'No uploads yet for this project.' : 'No uploads yet.'}
              </div>
            )
            : null}
        </div>
      </div>
    </div>
  );

  if (mobileStandalone) {
    return <div css={MOBILE_PAGE_STYLE}>{content}</div>;
  }

  return (
    <PageLayout title='Issue Video Upload' contentLayoutStyling='standard'>
      {content}
    </PageLayout>
  );
};
